import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef, useCallback, useState } from 'react';
import { fetchScans } from '../services/api/scansApi';
import { queryKeys } from '../lib/queryClient';

/**
 * Custom hook for infinite scroll pagination of scans
 * 
 * Features:
 * - Cursor-based pagination
 * - Automatic page fetching on scroll
 * - Optimistic updates
 * - Error handling
 * - Cache management
 * 
 * @param {Object} options - Hook options
 * @param {string} [options.status] - Filter by status (completed, running, failed, pending)
 * @param {number} [options.limit=20] - Items per page (1-100)
 * @param {boolean} [options.enabled=true] - Enable/disable query
 * @returns {Object} Query state and methods
 */
export const useInfiniteScans = ({ status, limit = 20, enabled = true } = {}) => {
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isPending,
    status: queryStatus,
    refetch,
  } = useInfiniteQuery({
    queryKey: queryKeys.scans.list({ status, limit }),
    queryFn: ({ pageParam = null, signal }) =>
      fetchScans({ cursor: pageParam, limit, status, signal }),
    getNextPageParam: (lastPage) => {
      // Return next cursor if more pages exist
      return lastPage.pagination?.has_more
        ? lastPage.pagination.next_cursor
        : undefined;
    },
    // Keep previous data while fetching new data (smooth UX)
    keepPreviousData: true,
    // Refetch on mount if data is stale
    refetchOnMount: true,
    // Cache for 1 minute
    staleTime: 60000,
    // Enable/disable query
    enabled,
  });

  // Flatten pages into single array of scans
  const scans = data?.pages.flatMap((page) => page.scans) ?? [];

  // Get total count from first page
  const totalCount = data?.pages[0]?.pagination?.total_count ?? 0;

  return {
    // Data
    scans,
    totalCount,

    // Status
    isLoading: queryStatus === 'loading' || queryStatus === 'pending' || (!data && (isFetching || isPending)),
    isError: queryStatus === 'error',
    isSuccess: queryStatus === 'success',
    isFetching,
    isFetchingNextPage,

    // Pagination
    hasNextPage,
    fetchNextPage,

    // Error
    error,

    // Methods
    refetch,
  };
};

/**
 * Custom hook for intersection observer (infinite scroll trigger)
 * 
 * Automatically triggers callback when target element becomes visible.
 * Used for implementing infinite scroll by observing a sentinel element.
 * 
 * @param {Function} callback - Function to call when element is visible
 * @param {Object} [options] - Intersection observer options
 * @param {Element} [options.root=null] - Root element for intersection
 * @param {string} [options.rootMargin='200px'] - Margin around root
 * @param {number} [options.threshold=0.1] - Visibility threshold (0-1)
 * @param {boolean} [options.enabled=true] - Enable/disable observer
 * @returns {React.RefObject} Ref to attach to sentinel element
 * 
 * Usage:
 * ```jsx
 * const sentinelRef = useIntersectionObserver(handleLoadMore);
 * return <div ref={sentinelRef}>Loading...</div>
 * ```
 */
export const useIntersectionObserver = (callback, options = {}) => {
  const {
    root = null,
    rootMargin = '200px', // Trigger 200px before reaching element
    threshold = 0.1,
    enabled = true,
  } = options;
  const [targetNode, setTargetNode] = useState(null);
  const callbackRef = useRef(callback);

  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    // Don't observe if disabled or no target
    if (!enabled || !targetNode) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        // Call callback when element is intersecting
        if (entry.isIntersecting) {
          callbackRef.current();
        }
      },
      {
        root,
        rootMargin,
        threshold,
      }
    );

    observer.observe(targetNode);

    // Cleanup
    return () => {
      observer.unobserve(targetNode);
    };
  }, [root, rootMargin, threshold, enabled, targetNode]);

  return setTargetNode;
};

/**
 * Custom hook for scroll restoration
 * 
 * Saves and restores scroll position when navigating away and back.
 * Useful for maintaining scroll position in infinite scroll lists.
 * 
 * @param {string} key - Unique key for storing scroll position
 * @returns {Object} Scroll restoration methods
 */
export const useScrollRestoration = (key) => {
  const scrollPositionRef = useRef(0);

  // Save scroll position
  const saveScrollPosition = useCallback(() => {
    scrollPositionRef.current = window.scrollY;
    sessionStorage.setItem(`scroll-${key}`, window.scrollY.toString());
  }, [key]);

  // Restore scroll position
  const restoreScrollPosition = useCallback(() => {
    const savedPosition = sessionStorage.getItem(`scroll-${key}`);
    if (savedPosition) {
      window.scrollTo(0, parseInt(savedPosition, 10));
    }
  }, [key]);

  // Clear saved position
  const clearScrollPosition = useCallback(() => {
    sessionStorage.removeItem(`scroll-${key}`);
  }, [key]);

  // Save scroll position on unmount
  useEffect(() => {
    return () => {
      saveScrollPosition();
    };
  }, [saveScrollPosition]);

  return {
    saveScrollPosition,
    restoreScrollPosition,
    clearScrollPosition,
  };
};

/**
 * Custom hook for debounced value
 * 
 * Returns a debounced version of the value that only updates after
 * the specified delay. Useful for search inputs and filters.
 * 
 * @param {any} value - Value to debounce
 * @param {number} [delay=500] - Delay in milliseconds
 * @returns {any} Debounced value
 */
export const useDebounce = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export default useInfiniteScans;

// Made with Bob
